"use client";
import { useState, useEffect, useRef } from "react";
import { z } from "zod";
import { useFieldArray, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
  DialogClose,
  DialogFooter,
} from "../../@/components/ui/dialog";
import { Button } from "../../@/components/ui/button";
import { Input } from "../../@/components/ui/input";
import { Form, FormField } from "@/components/ui/form";
import {
  Field,
  FieldContent,
  FieldError,
  FieldLabel,
} from "../../@/components/ui/field";
import { Textarea } from "../../@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../@/components/ui/select";
import { Plus, X, Edit2, Trash2, GripVertical, Info } from "lucide-react";
import axios from "axios";
import { api_endpoint } from "@/constants/constant";
import { useAuth } from "@clerk/clerk-react";
import { toast } from "sonner";
import MarkdownEditorDialog from "../components/MarkdownEditorDialog";
import { ClipLoader } from "react-spinners";
import QuestionFormCard from "../components/QuestionFormCard";
import { cn } from "@/lib/utils";

const questionSchema = z
  .object({
    id: z.string().optional(),
    type: z.enum(["multiple_choice", "true_false", "numeric"]),
    question_text: z.string().min(1, "Question is required"),
    score: z.number().min(1, "Score is required"),
    order: z.number().min(1, "Order is required"),
    question_content: z.string().optional(),
    explanation: z.string().optional(),
    answers: z
      .array(
        z.object({
          text: z.string().min(1, "Answer is required"),
          is_correct: z.boolean(),
        }),
      )
      .optional()
      .default([]),
    answer_text: z.string().optional(),
  })
  .refine(
    (data) => {
      if (data.type === "multiple_choice") {
        const validAnswers = (data.answers || []).filter(
          (a) => a.text && a.text.trim() !== "",
        );
        return (
          validAnswers.length >= 2 && validAnswers.some((a) => a.is_correct)
        );
      }
      if (data.type === "numeric") {
        const validAnswers = (data.answers || []).filter(
          (answer) => answer.text && answer.text.trim() !== "",
        );
        return (
          validAnswers.length >= 1 && validAnswers.some((a) => a.is_correct)
        );
      }
      if (data.type === "true_false") {
        const validAnswers = (data.answers || []).filter(
          (answer) => answer.text && answer.text.trim() !== "",
        );
        const texts = validAnswers.map((a) => a.text.toLowerCase());
        return (
          validAnswers.length === 2 &&
          texts.includes("true") &&
          texts.includes("false") &&
          validAnswers.some((a) => a.is_correct)
        );
      }
      return true;
    },
    {
      message: "Invalid answers for this question type",
      path: ["answers"],
    },
  );

const addQuizSchema = z.object({
  title: z.string().min(1, "Quiz title is required"),
  description: z.string().optional(),
  time_limit: z.coerce.number().min(0, "Time limit must be at least 0 minutes"),
  maximum_attempts: z.coerce
    .number()
    .min(0, "Maximum attempts must be at least 0"),
  passmark: z.coerce
    .number()
    .min(0)
    .max(100, "Passmark must be between 0 and 100"),
  questions: z.array(questionSchema).optional(),
});

export function UnifiedQuizDialog({
  topicId,
  onQuizAdded,
  refetchTopics,
  courseTitle = "default-course",
  courseSlug = "default-slug",
}) {
  const [open, setOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [showQuestionForm, setShowQuestionForm] = useState(false);
  const [markdownEditorOpen, setMarkdownEditorOpen] = useState(false);
  const [questionContentEditorOpen, setQuestionContentEditorOpen] =
    useState(false);
  const [explanationEditorOpen, setExplanationEditorOpen] = useState(false);
  const formContainerRef = useRef(null);
  const { getToken } = useAuth();

  const form = useForm({
    resolver: zodResolver(addQuizSchema),
    defaultValues: {
      title: "",
      description: "",
      time_limit: 0,
      maximum_attempts: 0,
      passmark: 0,
      questions: [],
    },
  });

  const {
    fields: questions,
    append,
    remove,
    update,
  } = useFieldArray({
    control: form.control,
    name: "questions",
  });

  const questionForm = useForm({
    resolver: zodResolver(questionSchema),
    defaultValues: {
      type: "multiple_choice",
      question_text: "",
      score: 1,
      order: 1,
      question_content: "",
      explanation: "",
      answers: [
        { text: "", is_correct: false },
        { text: "", is_correct: false },
      ],
      answer_text: "",
    },
  });

  const {
    fields: answers,
    append: appendAnswer,
    remove: removeAnswer,
    replace: replaceAnswers,
  } = useFieldArray({
    control: questionForm.control,
    name: "answers",
  });

  const questionType = questionForm.watch("type");
  const [correctAnswerIndex, setCorrectAnswerIndex] = useState(null);

  // Handlers for MarkdownEditorDialog
  const handleMarkdownEditorClose = () => {
    setMarkdownEditorOpen(false);
  };

  const handleMarkdownSave = (value) => {
    form.setValue("description", value);
    setMarkdownEditorOpen(false);
  };

  const handleOpenMarkdownEditor = () => {
    setMarkdownEditorOpen(true);
  };

  // Handlers for Question Content Markdown Editor
  const handleQuestionContentEditorClose = () => {
    setQuestionContentEditorOpen(false);
  };

  const handleQuestionContentSave = (value) => {
    questionForm.setValue("question_content", value);
    setQuestionContentEditorOpen(false);
  };

  const handleOpenQuestionContentEditor = () => {
    setQuestionContentEditorOpen(true);
  };

  // Handlers for Explanation Markdown Editor
  const handleExplanationEditorClose = () => {
    setExplanationEditorOpen(false);
  };

  const handleExplanationSave = (value) => {
    questionForm.setValue("explanation", value);
    setExplanationEditorOpen(false);
  };

  const handleOpenExplanationEditor = () => {
    setExplanationEditorOpen(true);
  };

  const handleNext = async () => {
    if (currentStep === 1) {
      const isValid = await form.trigger([
        "title",
        "description",
        "time_limit",
        "maximum_attempts",
        "passmark",
      ]);
      if (isValid) {
        setIsTransitioning(true);
        setTimeout(() => {
          setCurrentStep(2);
          setIsTransitioning(false);
          // Scroll to top of form container
          if (formContainerRef.current) {
            formContainerRef.current.scrollTop = 0;
          }
        }, 150);
      }
    }
  };

  const handleBack = () => {
    if (currentStep === 2) {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentStep(1);
        setIsTransitioning(false);
        if (formContainerRef.current) {
          formContainerRef.current.scrollTop = 0;
        }
      }, 150);
    }
  };

  const handleQuestionTypeChange = (newType) => {
    questionForm.setValue("type", newType);
    setCorrectAnswerIndex(null);

    if (newType === "numeric") {
      replaceAnswers([{ text: "", is_correct: false }]);
    } else if (newType === "true_false") {
      replaceAnswers([
        { text: "True", is_correct: false },
        { text: "False", is_correct: false },
      ]);
    } else if (newType === "multiple_choice") {
      if (answers.length < 2) {
        replaceAnswers([
          { text: "", is_correct: false },
          { text: "", is_correct: false },
        ]);
      }
    }
  };

  const handleCorrectAnswerChange = (index) => {
    setCorrectAnswerIndex(index);
    const currentAnswers = questionForm.getValues("answers");
    const updatedAnswers = currentAnswers.map((answer, i) => ({
      ...answer,
      is_correct: i === index,
    }));
    questionForm.setValue("answers", updatedAnswers);
  };

  const addQuestion = () => {
    setEditingQuestion(null);
    questionForm.reset({
      type: "multiple_choice",
      question_text: "",
      score: 1,
      order: questions.length + 1,
      question_content: "",
      explanation: "",
      answers: [
        { text: "", is_correct: false },
        { text: "", is_correct: false },
      ],
      answer_text: "",
    });
    setCorrectAnswerIndex(null);
    setShowQuestionForm(true);
  };

  const editQuestion = (index) => {
    const question = questions[index];
    setEditingQuestion(index);
    questionForm.reset(question);
    setCorrectAnswerIndex(
      question.answers?.findIndex((a) => a.is_correct) ?? null,
    );
    setShowQuestionForm(true);
  };

  const saveQuestion = (data) => {
    const questionData = {
      ...data,
      id:
        editingQuestion !== null
          ? questions[editingQuestion].id
          : Date.now().toString(),
    };

    if (editingQuestion !== null) {
      update(editingQuestion, questionData);
      toast.success("Question updated successfully!");
    } else {
      append(questionData);
      toast.success("Question added successfully!");
    }

    setShowQuestionForm(false);
    setEditingQuestion(null);
    questionForm.reset();
    setCorrectAnswerIndex(null);
  };

  const cancelQuestionEdit = () => {
    setShowQuestionForm(false);
    setEditingQuestion(null);
    questionForm.reset();
    setCorrectAnswerIndex(null);
  };

  const onSubmit = async (values) => {
    try {
      setIsSubmitting(true);

      const token = await getToken();
      const quizPayload = {
        topic_id: topicId,
        title: values.title,
        description: values.description || null,
        time_limit: values.time_limit,
        maximum_attempts: values.maximum_attempts,
        passmark: values.passmark,
      };
      const quizReqConfig = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };
      const quizResponse = await axios.post(
        api_endpoint + "/quiz/create",
        quizPayload,
        quizReqConfig,
      );
      const quizId = quizResponse.data.quiz?.id || quizResponse.data.id;

      for (const question of values.questions) {
        let answersArray = [];
        if (
          question.type === "multiple_choice" ||
          question.type === "numeric"
        ) {
          answersArray = question.answers.map((a) => ({
            text: a.text,
            is_correct: !!a.is_correct,
          }));
        } else if (question.type === "true_false") {
          answersArray = [
            {
              text: "True",
              is_correct:
                question.answers?.find((a) => a.text === "True")?.is_correct ||
                false,
            },
            {
              text: "False",
              is_correct:
                question.answers?.find((a) => a.text === "False")?.is_correct ||
                false,
            },
          ];
        } else if (question.type === "description" && question.answer_text) {
          answersArray = [{ text: question.answer_text, is_correct: true }];
        }

        const questionPayload = {
          quiz_id: quizId,
          question_text: question.question_text,
          question_content: question.question_content,
          type: question.type,
          order: question.order,
          score: question.score,
          explanation: question.explanation || null,
          answers: answersArray,
        };
        await axios.post(
          api_endpoint + "/quiz/question/create",
          questionPayload,
          quizReqConfig,
        );
      }

      if (onQuizAdded) onQuizAdded(values);
      if (refetchTopics) refetchTopics();
      toast.success("Quiz created successfully!");
      setOpen(false);
      setCurrentStep(1);
      form.reset();
    } catch (error) {
      console.error("Failed to create quiz:", error);
      toast.error("Failed to create quiz.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const totalPoints = questions.reduce((sum, q) => sum + (q.score || 0), 0);

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button variant="outline" className="">
            Add Quiz
            <Plus className="w-4 h-4 ml-1" />
          </Button>
        </DialogTrigger>

        <DialogContent className="h-auto max-h-[90vh] flex flex-col bg-(--surface-panel) text-(--neutral-900) border border-(--neutral-300) px-2 sm:px-6 overflow-hidden">
          <div
            ref={formContainerRef}
            className={`flex-1 overflow-y-auto transition-all duration-150 ease-in-out ${isTransitioning ? "opacity-0 translate-x-2" : "opacity-100 translate-x-0"}`}
          >
            {currentStep === 1 && (
              <div id="quiz-details-section">
                <DialogHeader>
                  <DialogTitle className="font-heading text-(--neutral-900)">
                    Add New Quiz
                  </DialogTitle>
                  <DialogDescription className="text-(--neutral-600)">
                    Create a quiz and add questions to the quiz
                  </DialogDescription>
                </DialogHeader>

                <Form {...form}>
                  <div className="space-y-6">
                    <h3 className="text-lg font-semibold text-(--neutral-900)">
                      Quiz Details
                    </h3>

                    <FormField
                      control={form.control}
                      name="title"
                      render={({ field, fieldState }) => (
                        <Field>
                          <FieldLabel>Quiz Title *</FieldLabel>
                          <FieldContent>
                            <Input
                              {...field}
                              placeholder="Intro to Machine Learning"
                              className={cn(
                                "bg-(--surface-panel) border border-(--neutral-300) text-(--neutral-900) placeholder:text-(--neutral-500) focus-visible:ring-0 mt-1",
                                fieldState.error &&
                                  "border-(--color-destructive) focus-visible:ring-(--color-primary)",
                              )}
                            />
                            <FieldError
                              errors={
                                fieldState.error
                                  ? [{ message: "This is required" }]
                                  : []
                              }
                            />
                          </FieldContent>
                        </Field>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="description"
                      render={({ field, fieldState }) => (
                        <Field>
                          <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                            <FieldLabel>
                              Description
                              <div className="flex items-center gap-1 text-xs text-(--neutral-600) font-body">
                                <Info className="w-3 h-3" />
                                Optional
                              </div>
                            </FieldLabel>
                          </div>
                          <FieldContent>
                            <Textarea
                              {...field}
                              rows={3}
                              className={cn(
                                "min-h-[120px] resize-none border border-(--neutral-300) bg-(--surface-panel) text-(--neutral-900) placeholder:text-(--neutral-500) focus:border-[var(--color-accent)] focus:ring-0 font-body mt-1",
                                fieldState.error &&
                                  "border-(--color-destructive) focus-visible:ring-(--color-primary)",
                              )}
                              placeholder="Brief description of what the quiz covers"
                            />
                            <FieldError errors={[]} />
                          </FieldContent>
                        </Field>
                      )}
                    />

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <FormField
                        control={form.control}
                        name="time_limit"
                        render={({ field, fieldState }) => (
                          <Field>
                            <FieldLabel>
                              Time Limit (Minutes) *
                              <div className="flex items-center gap-1 text-xs text-(--neutral-500) font-body">
                                (0 for unlimited)
                              </div>
                            </FieldLabel>
                            <FieldContent>
                              <Input
                                {...field}
                                type="number"
                                min="0"
                                className={cn(
                                  "bg-(--surface-panel) border border-(--neutral-300) text-(--neutral-900) focus-visible:ring-0 mt-1",
                                  fieldState.error &&
                                    "border-(--color-destructive) focus-visible:ring-(--color-primary)",
                                )}
                              />
                              <FieldError
                                errors={
                                  fieldState.error
                                    ? [{ message: "This is required" }]
                                    : []
                                }
                              />
                            </FieldContent>
                          </Field>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="maximum_attempts"
                        render={({ field, fieldState }) => (
                          <Field>
                            <FieldLabel>
                              Maximum Attempts *
                              <div className="flex items-center gap-1 text-xs text-(--neutral-500) font-body">
                                (0 for unlimited)
                              </div>
                            </FieldLabel>
                            <FieldContent>
                              <Input
                                {...field}
                                type="number"
                                min="0"
                                className={cn(
                                  "bg-(--surface-panel) border border-(--neutral-300) text-(--neutral-900) focus-visible:ring-0 mt-1",
                                  fieldState.error &&
                                    "border-(--color-destructive) focus-visible:ring-(--color-primary)",
                                )}
                              />
                              <FieldError
                                errors={
                                  fieldState.error
                                    ? [{ message: "This is required" }]
                                    : []
                                }
                              />
                            </FieldContent>
                          </Field>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="passmark"
                      render={({ field }) => {
                        const value = field.value || 0;
                        const fillPercent = Math.min(Math.max(value, 0), 100);

                        return (
                          <Field>
                            <FieldLabel>Passing Score *</FieldLabel>
                            <FieldContent>
                              <div className="flex items-center gap-3 mt-1">
                                <input
                                  type="number"
                                  min={0}
                                  max={100}
                                  value={value}
                                  onChange={(e) => {
                                    let newValue = Number(e.target.value);
                                    if (newValue > 100) newValue = 100;
                                    field.onChange(newValue);
                                  }}
                                  className={cn(
                                    "w-16 border border-(--neutral-300) rounded px-2 py-1 text-sm text-center bg-(--surface-panel) text-(--neutral-900)",
                                    form.formState.errors?.passmark &&
                                      "border-(--color-destructive)",
                                  )}
                                />
                                <div className="flex-1">
                                  <input
                                    type="range"
                                    min={0}
                                    max={100}
                                    value={value}
                                    onChange={(e) =>
                                      field.onChange(Number(e.target.value))
                                    }
                                    className="w-full h-2 bg-(--neutral-200) rounded-lg appearance-none cursor-pointer"
                                    style={{
                                      background: `linear-gradient(to right, var(--color-destructive) 0%, var(--color-destructive) ${fillPercent}%, var(--neutral-200) ${fillPercent}%, var(--neutral-200) 100%)`,
                                    }}
                                  />
                                </div>
                                <span className="text-sm text-(--neutral-600)">
                                  {value}%
                                </span>
                              </div>
                              <FieldError
                                errors={
                                  form.formState.errors?.passmark
                                    ? [{ message: "This is required" }]
                                    : []
                                }
                              />
                            </FieldContent>
                          </Field>
                        );
                      }}
                    />
                  </div>
                </Form>

                <DialogFooter className="mt-4">
                  <DialogClose asChild>
                    <Button variant="outline" className="">
                      Cancel
                    </Button>
                  </DialogClose>
                  <Button variant="" onClick={handleNext} className="">
                    Create Quiz
                  </Button>
                </DialogFooter>
              </div>
            )}

            {currentStep === 2 && (
              <div id="quiz-questions-section">
                <DialogHeader>
                  <DialogTitle className="font-heading text-(--neutral-900)">
                    Add Questions
                  </DialogTitle>
                  <DialogDescription className="text-(--neutral-600)">
                    {questions.length} questions • {totalPoints} points
                  </DialogDescription>
                </DialogHeader>

                <div className="flex-1 overflow-y-auto px-6 py-4">
                  <div className="space-y-4 mb-6">
                    {questions.map((question, index) => (
                      <QuestionCard
                        key={question.id || index}
                        question={question}
                        index={index}
                        onEdit={() => editQuestion(index)}
                        onRemove={() => remove(index)}
                      />
                    ))}

                    {questions.length === 0 && !showQuestionForm && (
                      <div className="text-center py-8 text-(--neutral-700) border-2 border-(--neutral-300) rounded-lg bg-(--surface-panel)">
                        No questions added yet. Click "Add Question" to get
                        started.
                      </div>
                    )}
                  </div>

                  {!showQuestionForm && (
                    <Button
                      onClick={addQuestion}
                      className="w-full"
                      variant="outline"
                    >
                      Add Question
                    </Button>
                  )}

                  <Dialog
                    open={showQuestionForm}
                    onOpenChange={setShowQuestionForm}
                  >
                    <QuestionFormCard
                      questionForm={questionForm}
                      answers={answers}
                      appendAnswer={appendAnswer}
                      removeAnswer={removeAnswer}
                      questionType={questionType}
                      correctAnswerIndex={correctAnswerIndex}
                      onTypeChange={handleQuestionTypeChange}
                      onCorrectAnswerChange={handleCorrectAnswerChange}
                      onSave={questionForm.handleSubmit(saveQuestion)}
                      onCancel={cancelQuestionEdit}
                      isEditing={editingQuestion !== null}
                      onOpenQuestionContentEditor={
                        handleOpenQuestionContentEditor
                      }
                      onOpenExplanationEditor={handleOpenExplanationEditor}
                    />
                  </Dialog>
                </div>

                <DialogFooter className="flex flex-row justify-between items-center gap-2 px-6 py-4 flex-wrap">
                  <Button onClick={handleBack} variant="outline" className="">
                    Back
                  </Button>
                  <div className="flex flex-row gap-2">
                    <DialogClose asChild>
                      <Button variant="outline" className="">
                        Cancel
                      </Button>
                    </DialogClose>
                    <Button
                      onClick={form.handleSubmit(onSubmit)}
                      disabled={isSubmitting || questions.length === 0}
                      className=""
                      variant=""
                    >
                      {isSubmitting ? (
                        <ClipLoader size={20} color="var(--neutral-50)" />
                      ) : (
                        "Create Quiz"
                      )}
                    </Button>
                  </div>
                </DialogFooter>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Render MarkdownEditorDialogs outside the main Dialog using portal pattern */}
      <MarkdownEditorDialog
        open={markdownEditorOpen}
        initialValue={form.watch("description") || ""}
        onClose={handleMarkdownEditorClose}
        onSave={handleMarkdownSave}
        contentFolder={courseSlug}
        contentName={courseTitle}
      />

      {/* Question Content Markdown Editor */}
      <MarkdownEditorDialog
        open={questionContentEditorOpen}
        initialValue={questionForm.watch("question_content") || ""}
        onClose={handleQuestionContentEditorClose}
        onSave={handleQuestionContentSave}
        contentFolder={courseSlug}
        contentName={courseTitle}
      />

      {/* Explanation Markdown Editor */}
      <MarkdownEditorDialog
        open={explanationEditorOpen}
        initialValue={questionForm.watch("explanation") || ""}
        onClose={handleExplanationEditorClose}
        onSave={handleExplanationSave}
        contentFolder={courseSlug}
        contentName={courseTitle}
      />
    </>
  );
}

// Question Card Component
function QuestionCard({ question, index, onEdit, onRemove }) {
  return (
    <div className="border border-(--neutral-300) rounded-lg p-2 sm:p-3 bg-(--surface-panel)">
      <div className="flex items-center mb-2 flex-nowrap overflow-x-auto gap-1 w-full">
        <GripVertical className="w-4 h-4 text-(--neutral-500) shrink-0" />
        <span className="text-xs font-medium uppercase bg-[var(--color-accent)] text-[var(--neutral-50)] px-2 py-1 rounded-full whitespace-nowrap">
          {question.type?.replace("_", " ") || "MULTIPLE CHOICE"}
        </span>
        <span className="text-xs font-medium text-[var(--color-accent)] uppercase border border-[var(--color-accent)] px-2 py-1 rounded-full whitespace-nowrap">
          {question.score} POINTS
        </span>
        <div className="flex-1" />
        <div className="flex items-center gap-1 ml-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={onEdit}
            className="text-(--neutral-500) hover:text-(--color-accent) px-1 shrink-0"
          >
            <Edit2 className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={onRemove}
            className="text-(--neutral-500) hover:text-(--color-destructive) px-1 shrink-0"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <h6 className="font-medium mb-2 text-(--neutral-900)">
        {question.question_text}
      </h6>

      {/* {question.question_content && (
                <div className="bg-white border rounded-md p-3 text-sm text-gray-700 mb-3">
                    {question.question_content}
                </div>
            )} */}

      {question.type !== "description" && question.answers && (
        <div className="space-y-2">
          {question.answers.map((answer, answerIndex) => (
            <div key={answerIndex} className="flex items-center gap-2">
              <div
                className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                  answer.is_correct
                    ? "bg-[var(--color-destructive)] border-(--color-destructive)"
                    : "border-(--neutral-300)"
                }`}
              >
                {answer.is_correct && (
                  <div className="w-2 h-2 bg-(--neutral-50) rounded-full" />
                )}
              </div>
              <span className="text-sm text-(--neutral-700)">
                {answer.text}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
